import React from 'react';
import * as S from './ProductSearchResultTile.style';
import {
    Flex,
    Image,
    Caption,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import type {
    ProductTaggingSearchResult,
    TagDataUseToCreateTag,
} from '@modules/product-tagging/type';
import { convertSearchResultToTagType } from '../../util/convert-search-result-to-tag-type';
import { getUsesNeutralMultiplyTreatment } from '../../util/get-uses-neutral-multiply-treatment';
import { needsDefaultBorder } from '@rocco/utils/colour-helpers';

interface ProductSearchResultTileProps {
    tileData: ProductTaggingSearchResult;
    onConfirm: (data: TagDataUseToCreateTag) => void;
    showProfessionalName?: boolean;
}

const ProductSearchResultTile = ({
    tileData,
    showProfessionalName,
    onConfirm,
}: ProductSearchResultTileProps) => {
    const logoBackground = tileData.logoBackground;
    const isProfessional = tileData.pType === 'Professional';
    const usesNeutralMultiply = getUsesNeutralMultiplyTreatment(tileData);
    const useDefaultBorder =
        isProfessional && needsDefaultBorder(logoBackground);

    const { css } = useStyles({
        logoBackground,
        isProfessional,
        usesNeutralMultiply,
        useDefaultBorder,
    });

    const data = convertSearchResultToTagType(tileData);

    return (
        <Flex
            gap={'space-8'}
            className={css(S.ResultTile)}
            onClick={() => onConfirm(data)}
        >
            <div className={css(S.ResultTileThumb)}>
                <Image
                    src={tileData.thumb}
                    width={isProfessional ? 40 : 48}
                    height={isProfessional ? 40 : 48}
                    layout="fixed"
                    objectFit={'contain'}
                    alt={tileData.title ?? ''}
                    mixBlendMode={usesNeutralMultiply ? 'multiply' : undefined}
                    styles={
                        usesNeutralMultiply
                            ? {
                                  root: {
                                      borderRadius: isProfessional
                                          ? '50%'
                                          : pxToRem(8),
                                      overflow: 'hidden',
                                      background: 'transparent',
                                  },
                              }
                            : undefined
                    }
                    placeholderColor={
                        isProfessional && !usesNeutralMultiply
                            ? 'transparent'
                            : undefined
                    }
                />
            </div>
            <Flex column className={css(S.ResultTileContent)}>
                <Caption variant="04" className={css(S.ResultTileTitle)}>
                    {tileData.title}
                </Caption>
                {tileData.professional && showProfessionalName && (
                    <Caption
                        variant="04"
                        className={css(S.ResultTileProfessional)}
                    >
                        {tileData.professional}
                    </Caption>
                )}
            </Flex>
        </Flex>
    );
};

export default ProductSearchResultTile;
